import { ChevronRightIcon, ClockIcon, SearchIcon } from 'lucide-react';
import { useId, useMemo, useState, type FC } from 'react';
import { Button } from './button';
import { groupChatHistory, type ChatHistoryItem } from './chat-history-groups';
import { ChatHistoryRow } from './chat-history-row';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

interface ChatHistoryProps {
  items: readonly ChatHistoryItem[];
  /** The conversation on screen; highlighted in the list. */
  activeId?: string;
  onSelect: (id: string) => void;
  onTogglePin?: (id: string) => void;
  onArchive?: (id: string, archived: boolean) => void;
  onDelete?: (id: string) => void;
  /** Reference time for the date groups. Pass a fixed value in stories and tests. */
  now?: number;
  className?: string;
}

const matches = (item: ChatHistoryItem, query: string) =>
  item.title.toLowerCase().includes(query.trim().toLowerCase());

/**
 * Past conversations behind a clock button: searchable, grouped by recency
 * with pinned ones first, and an Archived section folded away at the bottom.
 * Picking one closes the popover.
 */
export const ChatHistory: FC<ChatHistoryProps> = ({
  items,
  activeId,
  onSelect,
  onTogglePin,
  onArchive,
  onDelete,
  now,
  className
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchId = useId();
  const visible = useMemo(
    () => items.filter((item) => matches(item, query)),
    [items, query]
  );
  const groups = useMemo(() => groupChatHistory(visible, now), [visible, now]);
  const archived = visible.filter((item) => item.archived);
  const rowProps = { activeId, onTogglePin, onArchive, onDelete };

  const select = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="History"
              className={
                className ??
                'size-11 rounded-lg text-[var(--z-text-secondary)] hover:border-border hover:text-foreground aria-expanded:bg-surface-hover aria-expanded:text-foreground sm:size-8'
              }
            >
              <ClockIcon />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>History</TooltipContent>
      </Tooltip>
      <PopoverContent
        aria-label="Conversation history"
        align="end"
        sideOffset={6}
        className="w-[min(22rem,calc(100vw-2rem))] gap-0 rounded-2xl border border-border-strong bg-[var(--z-surface-solid)] p-1.5 shadow-2xl ring-0"
      >
        <label
          htmlFor={searchId}
          className="flex items-center gap-2 border-b border-[var(--z-border-subtle)] px-2.5 pt-1.5 pb-2.5"
        >
          <SearchIcon
            aria-hidden="true"
            className="size-3.5 shrink-0 text-muted-foreground"
          />
          <span className="sr-only">Search conversations</span>
          <input
            id={searchId}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="Search conversations…"
            className="min-w-0 flex-1 bg-transparent text-body text-foreground outline-none placeholder:text-[var(--z-text-disabled)] [&::-webkit-search-cancel-button]:hidden"
          />
        </label>
        <div className="max-h-[min(24rem,60vh)] overflow-y-auto pt-1">
          {groups.map((group) => (
            <section key={group.label} aria-label={group.label} className="pt-1.5">
              <h3 className="px-2.5 pb-1 text-caption font-medium text-muted-foreground">
                {group.label}
              </h3>
              <ul className="flex flex-col">
                {group.items.map((item) => (
                  <ChatHistoryRow
                    key={item.id}
                    item={item}
                    isActive={item.id === rowProps.activeId}
                    onSelect={select}
                    onTogglePin={rowProps.onTogglePin}
                    onArchive={rowProps.onArchive}
                    onDelete={rowProps.onDelete}
                  />
                ))}
              </ul>
            </section>
          ))}
          {groups.length === 0 && archived.length === 0 ? (
            <p className="px-2.5 py-6 text-center text-body text-muted-foreground">
              {query ? 'No conversations match.' : 'No conversations yet.'}
            </p>
          ) : null}
          {archived.length > 0 ? (
            <Collapsible
              defaultOpen={false}
              className="mt-1.5 border-t border-[var(--z-border-subtle)] pt-1.5"
            >
              <CollapsibleTrigger className="group/archived flex w-full cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-2 text-left text-body text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground">
                <ChevronRightIcon
                  aria-hidden="true"
                  className="size-3.5 transition-transform duration-150 group-aria-expanded/archived:rotate-90"
                />
                Archived
                <span className="ml-auto text-caption">{archived.length}</span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="flex flex-col">
                  {archived.map((item) => (
                    <ChatHistoryRow
                      key={item.id}
                      item={item}
                      isActive={item.id === rowProps.activeId}
                      onSelect={select}
                      onArchive={rowProps.onArchive}
                      onDelete={rowProps.onDelete}
                    />
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};
